import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { products } from '../utils/data';
import FormPopup from '../components/FormPopup';
import HeroSection from '../components/HeroSection';

const Products: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Helper function to get product signature feature
  const getSignatureFeature = (productId: string) => {
    switch (productId) {
      case 'pro-8k-projector':
        return { label: 'Native 8K', description: 'Resolution' };
      case 'immersion-surround':
        return { label: '5.1 Channel', description: 'Surround System' };
      case 'studio-soundbar':
        return { label: '12 Drivers', description: 'Precision Array' };
      case 'vision-screen':
        return { label: 'Acoustic Transparent', description: 'Material Technology' };
      case 'tower-monitor':
        return { label: '2000W Peak', description: 'Bi-Amped Class D' };
      default:
        return { label: 'Premium', description: 'Engineering' };
    }
  };

  return (
    <div className="min-h-screen">
      {/* LUXURY SHOWROOM HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-2.png"
        mobileImage="/images/mobile-hero-2.png"
        title={<span className="text-gradient">The Collection</span>}
        subtitle="Four masterpieces of engineering and artistry, each designed to transform your space into a sanctuary of cinematic excellence. Welcome to the pinnacle of home entertainment."
        tagline="Where Engineering Meets Artistry"
      />

      {/* PRODUCT CATEGORIES SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Precision-Engineered Excellence
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Each component in our collection represents years of obsessive engineering 
              and artistic vision, designed to work in perfect harmony or stand alone 
              as individual masterpieces.
            </motion.p>
          </div>
          
          {/* Category Overview Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <motion.div 
              className="text-center glass-effect rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-highlight text-2xl">📽️</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gradient">Projection</h3>
              <p className="text-secondary/80 text-sm">8K Visual Perfection</p>
            </motion.div>
            
            <motion.div 
              className="text-center glass-effect rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-highlight text-2xl">🔊</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gradient">Surround Audio</h3>
              <p className="text-secondary/80 text-sm">Reference-Quality Sound</p>
            </motion.div>
            
            <motion.div 
              className="text-center glass-effect rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-highlight text-2xl">📻</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gradient">Soundbar</h3>
              <p className="text-secondary/80 text-sm">Minimalist Luxury</p>
            </motion.div>
            
            <motion.div 
              className="text-center glass-effect rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-highlight text-2xl">🖼️</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gradient">Projection Screen</h3>
              <p className="text-secondary/80 text-sm">Acoustic Transparency</p>
            </motion.div>

            <motion.div
              className="text-center glass-effect rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-highlight text-2xl">🔉</span>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gradient">Studio Monitor</h3>
              <p className="text-secondary/80 text-sm">Studio-Grade Precision</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FLAGSHIP PRODUCT SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            {products.map((product, index) => (
              <motion.div 
                key={product.id} 
                className={`mb-20 ${index !== 0 ? 'pt-20' : ''}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  
                  {/* Product Information */}
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="mb-4">
                      <span className="text-highlight/80 text-sm uppercase tracking-wider">
                        {product.category}
                      </span>
                    </div>
                    
                    <h2 className="text-4xl md:text-5xl font-light mb-4 text-gradient">
                      {product.name}
                    </h2>
                    
                    <p className="text-xl text-highlight/90 mb-6 font-light italic">
                      {product.tagline}
                    </p>
                    
                    <p className="text-lg text-secondary/90 mb-8 leading-relaxed">
                      {product.description}
                    </p>
                    
                    {/* Key Features */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 text-gradient">Key Features</h3>
                      <div className="space-y-2">
                        {product.features.slice(0, 3).map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center">
                            <div className="w-2 h-2 bg-highlight rounded-full mr-3"></div>
                            <span className="text-secondary/80">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Pricing and CTA */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                      <div className="mb-4 sm:mb-0">
                        <div className="text-2xl font-light text-gradient">{product.priceRange}</div>
                        <div className="text-secondary/60 text-sm">Starting investment</div>
                      </div>
                      
                      <div className="flex gap-4">
                        <Link 
                          to={`/products/${product.id}`}
                          className="btn-primary px-6 py-3 inline-block text-center"
                        >
                          Explore Details
                        </Link>
                        <button 
                          onClick={() => setIsFormOpen(true)}
                          className="btn-secondary px-6 py-3"
                        >
                          Schedule Consultation
                        </button>
                      </div>
                    </div>
                    
                    {/* Awards */}
                    {product.awards && product.awards.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-secondary/20">
                        <div className="text-highlight/80 text-xs uppercase tracking-wider mb-2">
                          Industry Recognition
                        </div>
                        <div className="text-secondary/70 text-sm">
                          {product.awards[0]}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Visual */}
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <div className="relative">
                      <motion.div 
                        className="relative max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        viewport={{ once: true }}
                      >
                        {/* Clean image presentation with subtle border */}
                        <div className="relative group">
                          {product.id === 'studio-soundbar' ? (
                            // Soundbar with seamless gray background
                            <>
                              <img 
                                src="/images/parallax-cinema-soundbar-image.png"
                                alt={product.name}
                                className="w-full h-auto rounded-lg"
                                loading="lazy"
                                decoding="async"
                              />
                              {/* Subtle hover effect overlay */}
                              <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                            </>
                          ) : (
                            // Other products with gray border
                            <>
                              <div className="bg-[#2A2A2A] rounded-lg p-4 border border-[#2A2A2A]">
                                <img 
                                  src={
                                    product.id === 'pro-8k-projector' ? '/images/parallax-pro-8k-projector-branded.jpg' :
                                    product.id === 'immersion-surround' ? '/images/parallax-surround-sound-gray.png' :
                                    product.id === 'vision-screen' ? '/images/parallax-card-image.png' :
                                    product.id === 'tower-monitor' ? '/images/parallax-tower-monitor-pair.png' :
                                    ''
                                  }
                                  alt={product.name}
                                  className="w-full h-auto rounded"
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                              {/* Subtle hover effect overlay */}
                              <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                            </>
                          )}
                        </div>
                        
                        {/* Premium caption styling */}
                        <motion.div 
                          className="mt-8 text-center pb-8"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ duration: 1, delay: 0.5 }}
                          viewport={{ once: true }}
                        >
                          <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                            Handcrafted Excellence
                          </div>
                          <div className="text-secondary/40 text-sm uppercase tracking-wider">
                            {product.name}
                          </div>
                        </motion.div>
                      </motion.div>
                      
                      {/* Floating specification highlight */}
                      <div className="absolute top-4 right-4 glass-effect rounded-lg p-4 backdrop-blur-md z-10">
                        <div className="text-highlight text-lg font-light">
                          {getSignatureFeature(product.id).label}
                        </div>
                        <div className="text-secondary/60 text-xs uppercase tracking-wider">
                          {getSignatureFeature(product.id).description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CONSULTATION CALL TO ACTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Begin Your Cinematic Journey
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 mb-12 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Experience our collection in our private showroom, or schedule a consultation 
              to design the perfect cinematic sanctuary for your space.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <button 
                onClick={() => setIsFormOpen(true)}
                className="btn-primary text-lg px-12 py-4"
              >
                Visit Our Showroom
              </button>
              <button 
                onClick={() => setIsFormOpen(true)}
                className="btn-secondary text-lg px-12 py-4"
              >
                Schedule Consultation
              </button>
            </motion.div>
            
            {/* Contact Information */}
            <motion.div 
              className="glass-effect rounded-xl p-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-6 text-gradient">Private Showroom</h3>
              <div className="grid md:grid-cols-2 gap-6 text-secondary/80">
                <div>
                  <div className="font-medium mb-1">Location</div>
                  <div className="text-sm">By Appointment Only</div>
                </div>
                <div>
                  <div className="font-medium mb-1">Consultation</div>
                  <div className="text-sm">Complimentary Design Service</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FormPopup */}
      <FormPopup 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
      />
    </div>
  );
};

export default Products; 
